import React from 'react';
import {Document, Page, Text, StyleSheet, Image} from '@react-pdf/renderer';
import {Trans, useTranslation} from 'react-i18next';

const HEADING_TEXT_SIZE = 24;
const BODY_TEXT_SIZE = 14;
const FONT_FAMILY = 'Times-Roman';
const PAGE_MARGIN = 12;
const SIGNATURE_MAX_HEIGHT = 50;
const TEXT_VERTICAL_MARGIN = 6;
const IMAGE_VERTICAL_MARGIN = 15;
const LIST_ITEM_LEFT_MARGIN = PAGE_MARGIN + 6;
const LIST_ITEM_VERTICAL_MARGIN = 4;

const styles = StyleSheet.create({
  body: {
    paddingTop: 35,
    paddingBottom: 65,
    paddingHorizontal: 35
  },
  title: {
    fontSize: HEADING_TEXT_SIZE,
    fontFamily: FONT_FAMILY,
    margin: PAGE_MARGIN
  },
  subtitle: {
    fontSize: 18,
    margin: PAGE_MARGIN,
    fontFamily: FONT_FAMILY
  },
  text: {
    margin: PAGE_MARGIN,
    marginBottom: TEXT_VERTICAL_MARGIN,
    marginTop: TEXT_VERTICAL_MARGIN,
    fontSize: BODY_TEXT_SIZE,
    textAlign: 'justify',
    fontFamily: FONT_FAMILY
  },
  image: {
    marginVertical: IMAGE_VERTICAL_MARGIN,
    marginHorizontal: PAGE_MARGIN,
    maxHeight: SIGNATURE_MAX_HEIGHT,
    marginRight: 'auto'
  },
  highlightedUnderlinedText: {
    fontWeight: 'bold',
    textDecoration: 'underline'
  },
  highlightedText: {
    fontWeight: 'bold'
  },
  listItem: {
    marginRight: PAGE_MARGIN,
    marginLeft: LIST_ITEM_LEFT_MARGIN,
    marginBottom: LIST_ITEM_VERTICAL_MARGIN,
    marginTop: LIST_ITEM_VERTICAL_MARGIN,
    fontSize: BODY_TEXT_SIZE,
    textAlign: 'justify',
    fontFamily: FONT_FAMILY
  }
});
const ShingrixConsentFormDoc = ({
  date,
  time,
  signatureURL,
  vaccine
}: {
  date: string;
  time: string;
  signatureURL: string;
  vaccine: string;
}): JSX.Element => {
  const {t} = useTranslation();
  return (
    <Document>
      <Page style={styles.body}>
        <Text style={styles.title}>
          {t('APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.TITLE')}
        </Text>
        <Text style={styles.text}>
          {t('APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.CDC_INFO')}
        </Text>
        <Text style={{...styles.text, ...styles.highlightedUnderlinedText}}>
          {t(
            'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.SINGRIX_VACCINE_TITLE'
          )}
        </Text>
        <Text style={styles.text}>
          {t(
            'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.SINGRIX_VACCINE_INFO'
          )}
        </Text>
        <Text style={styles.text}>
          {t(
            'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.VACCINE_INFORMATION_SHEET_INFO'
          )}
        </Text>
        <Text style={{...styles.text, ...styles.listItem}}>
          {t(
            'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.VACCINE_INFORMATION_SHEET_ITEMS.INFORMATION_ITEM_1'
          )}
        </Text>
        <Text style={{...styles.text, ...styles.listItem}}>
          {t(
            'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.VACCINE_INFORMATION_SHEET_ITEMS.INFORMATION_ITEM_2'
          )}
        </Text>
        <Text style={{...styles.text, ...styles.listItem}}>
          {t(
            'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.VACCINE_INFORMATION_SHEET_ITEMS.INFORMATION_ITEM_3'
          )}
        </Text>
        <Text style={{...styles.text, ...styles.listItem}}>
          {t(
            'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.VACCINE_INFORMATION_SHEET_ITEMS.INFORMATION_ITEM_4'
          )}
        </Text>
        <Text style={styles.text}>
          {t(
            'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.AUTHORIZE_RELEASE_OF_MEDICAL_INFO'
          )}
        </Text>
        <Text style={styles.text}>
          <Trans
            i18nKey={
              'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.UNDERSTAND_BENEFITS_AND_RISKS'
            }
            components={[<Text style={styles.highlightedText} key="X" />]}
          />
        </Text>
        <Text style={styles.text}>
          {t(
            'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.PLEASE_CHECK_THE_VACCINE'
          )}
        </Text>
        {vaccine === 'shingrix' && (
          <Text style={styles.text}>
            {t(
              'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.SHINGRIX_VACCINE_LABEL'
            )}
          </Text>
        )}
        {vaccine === 'no' && (
          <Text style={styles.text}>
            <Trans
              i18nKey={
                'APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.NO_VACCINE'
              }
              components={[<span style={styles.highlightedText} key="X" />]}
            />
          </Text>
        )}
        <Image style={styles.image} src={signatureURL} />
        <Text style={styles.text}>
          {t('APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.DATE')}:{' '}
          {date}
        </Text>
        <Text style={styles.text}>
          {t('APPOINTMENTS_SCHEDULAR_PORTAL.SHINGRIX_CONSENT_FORM.TIME')}:{' '}
          {time}
        </Text>
      </Page>
    </Document>
  );
};
export default ShingrixConsentFormDoc;
